import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getAliases, getRegions, getCities, createAlias, deleteAlias } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Trash2, Loader2, Search, Tags } from 'lucide-react';
import { toast } from 'sonner';

export default function AliasesPage() {
  const { t, language } = useLanguage();
  const [aliases, setAliases] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAlias, setDeletingAlias] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    alias_value: '',
    entity_type: 'region',
    master_id: '',
    master_name: '',
  });

  const fetchData = async () => {
    try {
      const [aliasesRes, regionsRes, citiesRes] = await Promise.all([
        getAliases(),
        getRegions(),
        getCities()
      ]);
      setAliases(aliasesRes.data);
      setRegions(regionsRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getMasterOptions = () => {
    switch (formData.entity_type) {
      case 'region':
        return regions;
      case 'city':
        return cities;
      default:
        return [];
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      alias_value: '',
      entity_type: 'region',
      master_id: '',
      master_name: '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.alias_value.trim() || !formData.master_id) {
      toast.error(t('validation.required'));
      return;
    }
    
    const masterOptions = getMasterOptions();
    const master = masterOptions.find(m => m.id === formData.master_id);
    
    setSaving(true);
    try {
      await createAlias({
        ...formData,
        master_name: master?.name_ar || ''
      });
      toast.success(t('success.created'));
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingAlias) return;
    
    try {
      await deleteAlias(deletingAlias.id);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeletingAlias(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    }
  };

  const filteredAliases = aliases.filter(alias => {
    const matchesSearch = alias.alias_value?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alias.master_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || alias.entity_type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="aliases-page">
      {/* Header */}
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <CardTitle className="flex items-center gap-2">
              <Tags className="w-5 h-5" />
              {t('aliases')}
            </CardTitle>
            <Button 
              onClick={handleOpenDialog}
              className="bg-[#0A4C3A] hover:bg-[#073B2D]"
              data-testid="add-alias-button"
            >
              <Plus className="w-4 h-4 me-2" />
              {t('add_alias')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ps-10"
                data-testid="aliases-search"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-40" data-testid="aliases-filter-type">
                <SelectValue placeholder={t('entity_type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="region">{t('region')}</SelectItem>
                <SelectItem value="city">{t('city')}</SelectItem>
                <SelectItem value="district">{t('district')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">{t('alias_value')}</TableHead>
                <TableHead className="font-medium">{t('entity_type')}</TableHead>
                <TableHead className="font-medium">{t('master_name')}</TableHead>
                <TableHead className="font-medium">{t('created_at')}</TableHead>
                <TableHead className="font-medium text-center">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAliases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    {t('no_data')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAliases.map((alias) => (
                  <TableRow key={alias.id} data-testid={`alias-row-${alias.id}`}>
                    <TableCell className="font-medium">{alias.alias_value}</TableCell>
                    <TableCell>{t(alias.entity_type)}</TableCell>
                    <TableCell>{alias.master_name}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {alias.created_at ? new Date(alias.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeletingAlias(alias);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-alias-${alias.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('add_alias')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="alias_value">{t('alias_value')} *</Label>
              <Input
                id="alias_value"
                value={formData.alias_value}
                onChange={(e) => setFormData({ ...formData, alias_value: e.target.value })}
                placeholder="مثال: مكة المكرمة"
                data-testid="alias-value-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('entity_type')} *</Label>
              <Select 
                value={formData.entity_type} 
                onValueChange={(value) => setFormData({ ...formData, entity_type: value, master_id: '' })}
              >
                <SelectTrigger data-testid="alias-entity-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="region">{t('region')}</SelectItem>
                  <SelectItem value="city">{t('city')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('master_record')} *</Label>
              <Select 
                value={formData.master_id} 
                onValueChange={(value) => setFormData({ ...formData, master_id: value })}
              >
                <SelectTrigger data-testid="alias-master-select">
                  <SelectValue placeholder={t('select')} />
                </SelectTrigger>
                <SelectContent>
                  {getMasterOptions().map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {language === 'ar' ? item.name_ar : (item.name_en || item.name_ar)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#0A4C3A] hover:bg-[#073B2D]"
              data-testid="save-alias-button"
            >
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingAlias?.alias_value} → {deletingAlias?.master_name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-alias"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
